import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { CommonModule } from '@angular/common';
import { NotificationService } from '../../services/notification.service'; // NotificationService;

/**
 * ResendEmailNotificationComponent: allows a user to request a new password reset email and enforces a short cooldown so the action cannot be spammed.
 * Purpose: Reads an optional email from the route query parameters, calls AuthService.forgotPassword to resend a password reset link, shows success/error messages via NotificationService, and starts a 30-second cooldown during which the resend action is disabled.
 * Key methods:
 *   - resendEmail(): Sends the resend request, handles success and error responses, updates UI state and triggers messaging.
 *   - startCooldownTimer(): Starts a 30-second countdown, updates cooldownTimeLeft each second, and clears the interval when finished.
 * Important lifecycle hooks:
 *   - ngOnInit(): Subscribes to ActivatedRoute.queryParams to retrieve the email; redirects to /forgot-password if the email is missing.
 *   - ngOnDestroy(): Clears any active cooldown timer to avoid memory leaks.
 * @component
 * @selector app-resend-email-notification
 * @example
 *   <app-resend-email-notification></app-resend-email-notification>
 * @input {string | null} email - Optional email address obtained from the route query parameters; used to target the resend request when present.
 * @output {none} none - This component does not emit Angular Output events; it communicates status via NotificationService and by navigating the router when needed.
 */
@Component({
  selector: 'app-resend-email-notification',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './resend-email-notification.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './resend-email-notification.component.scss',
})
export class ResendEmailNotificationComponent implements OnInit, OnDestroy {
  email: string | null = null;
  loading = false;
  showComponent = true;
  successMessage: string | null = null;

  // Timer properties
  isOnCooldown = false;
  cooldownTimeLeft = 0;
  private cooldownTimer: any;

  constructor(
    private route: ActivatedRoute,
    private authService: AuthService,
    private router: Router,
    private messageService: NotificationService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.email = params['email'];
      if (!this.email) {
        this.router.navigate(['/forgot-password'], { queryParams: { email: this.email } });
      }
    });
  }

  /**
   * Sends a password reset email if an email address is present and the resend action is not on cooldown.
   * @example
   * esendEmail()
   * undefined
   * @param {void} none - This method does not accept any parameters.
   * @returns {void} Performs side effects only: toggles loading state, displays messages, and starts a cooldown timer.
   **/
  resendEmail() {
    if (!this.email || this.isOnCooldown) {
      // Handle case where email is not available or button is on cooldown
      return;
    }

    this.loading = true;
    this.authService.forgotPassword(this.email).subscribe({
      next: response => {
        this.loading = false;

        if (response.status === true) {
          this.showComponent = false;
          this.successMessage = 'A new password reset link has been sent to your email.';
          this.messageService.add({
            severity: 'success',
            summary: 'Password reset email sent',
            detail: 'We have sent the link again. Check your inbox.',
            life: 3000,
          });

          // Start 30-second cooldown timer
          this.startCooldownTimer();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.message || 'Failed to send email.',
            life: 3000,
          });
        }
      },
      error: err => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'An error occurred. Please try again.',
        });
      },
    });
  }

  /**
   * Start a 30-second cooldown on the component, decrementing cooldownTimeLeft every second and clearing the interval when complete.
   * @example
   * tartCooldownTimer()
   * // undefined
   * @returns {void} Does not return a value; updates this.isOnCooldown, this.cooldownTimeLeft, and manages this.cooldownTimer.
   */
  startCooldownTimer() {
    this.isOnCooldown = true;
    this.cooldownTimeLeft = 30;

    this.cooldownTimer = setInterval(() => {
      this.cooldownTimeLeft--;

      if (this.cooldownTimeLeft <= 0) {
        this.isOnCooldown = false;
        this.cooldownTimeLeft = 0;
        if (this.cooldownTimer) {
          clearInterval(this.cooldownTimer);
          this.cooldownTimer = null;
        }
      }
    }, 1000);
  }

  ngOnDestroy(): void {
    // Clean up the timer when component is destroyed
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer);
    }
  }
}
